import React from 'react';
import { motion } from 'framer-motion';
import { ChefHat, Heart, Award, Users, Target, Clock } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem, SlideIn } from '../components/PageTransition';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { BengaliDecorativeElement, BengaliCornerDecor, BengaliDivider } from '../components/BengaliPatterns';
import about from "../assets/about.mp4"
export const AboutPage = () => {
  const features = [
    {
      icon: ChefHat,
      title: "Expert Chefs",
      description: "Trained in traditional Bengali cooking techniques with decades of experience"
    },
    {
      icon: Heart,
      title: "Made with Love",
      description: "Every dish prepared with passion, care, and authentic family recipes"
    },
    {
      icon: Award,
      title: "Award Winning",
      description: "Recognized for authentic Bengali cuisine and exceptional service"
    },
    {
      icon: Users,
      title: "Family Owned",
      description: "Three generations of culinary excellence and hospitality"
    }
  ];

  const values = [
    {
      icon: Target,
      title: "Authenticity",
      description: "We stay true to traditional Bengali recipes and cooking methods"
    },
    {
      icon: Heart,
      title: "Quality",
      description: "Only the freshest ingredients sourced daily for the best taste"
    },
    {
      icon: Users,
      title: "Community",
      description: "Building connections through the shared love of Bengali food"
    },
    {
      icon: Clock,
      title: "Excellence",
      description: "Committed to delivering exceptional dining experiences every time"
    }
  ];

  const milestones = [
    { year: 2009, value: 15, label: 'Years of Excellence' },
    { year: 2015, value: 500, suffix: '+', label: 'Happy Customers' },
    { year: 2020, value: 50, suffix: '+', label: 'Signature Dishes' },
    { year: 2024, value: 25, suffix: '+', label: 'Expert Team' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        >
          <img
            src="https://images.unsplash.com/photo-1582228096960-7f5d2ec4aa8e"
            alt="About Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
        </motion.div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center">
          <motion.h1
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Story
          </motion.h1>
          <motion.p
            className="text-xl text-gray-200 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            A journey of passion, tradition, and authentic Bengali flavors
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 relative overflow-hidden" style={{background:"rgb(244 242 242)"}}>
        {/* <BengaliDecorativeElement pattern="alpona" /> */}
        <BengaliCornerDecor />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <SlideIn direction="left">
              <div>
                <h2 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-6">
                  About Just Bengali
                </h2>
                <div className="w-24 h-1 bg-amber-600 mb-6"></div>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  At <span className="font-semibold text-amber-800">Just Bengali</span>, we bring you the rich culinary heritage of Bengal. Our journey began over 15 years ago with a simple dream: to share the authentic taste of Bengali home cooking with the world.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  From the famous Hilsa fish curry to the sweetest Mishti Doi, every dish tells a story of Bengal's vibrant culture and tradition. Our chefs use traditional recipes passed down through generations, combined with the freshest ingredients, to create flavors that transport you straight to the heart of Bengal.
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  We pride ourselves on maintaining authenticity while serving with modern hospitality. Each meal is prepared with love, care, and the same attention to detail that you'd find in a Bengali household.
                </p>
              </div>
            </SlideIn>

            <SlideIn direction="right">
              {/* <div className=" gap-4">
                <motion.div
                  className="h-64 rounded-2xl overflow-hidden shadow-xl"
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  transition={{ duration: 0.3 }}
                > */}
                <video
  src={about} // your video file path
  autoPlay
  loop
  muted
  playsInline
  className="sm:ml-0 lg:ml[190px]"
  style={{width:"28rem", height:"28rem"}}
  // className="w-full h-full object-cover"
></video>
                {/* </motion.div>
             
              </div> */}
            </SlideIn>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-amber-50/50 relative overflow-hidden">
        <BengaliDecorativeElement pattern="paisley" className="opacity-30" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4">
                What Makes Us Special
              </h2>
              <div className="w-24 h-1 bg-amber-600 mx-auto"></div>
            </div>
          </FadeIn>

          <StaggerContainer staggerDelay={0.15}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <StaggerItem key={index}>
                  <motion.div
                    className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
                    whileHover={{ y: -10 }}
                  >
                    <motion.div
                      className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6 mx-auto"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <feature.icon className="w-8 h-8 text-amber-700" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-amber-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>

      {/* Milestones Section */}
      <section className="py-20 bg-gradient-to-r from-amber-900 to-amber-800 relative overflow-hidden">
        <BengaliDecorativeElement pattern="lotus" className="opacity-10" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
                Our Journey in Numbers
              </h2>
              <div className="w-24 h-1 bg-amber-300 mx-auto"></div>
            </div>
          </FadeIn>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl font-bold text-amber-300 mb-2">
                  <AnimatedCounter
                    end={milestone.value}
                    suffix={milestone.suffix || ''}
                    duration={2.5}
                  />
                </div>
                <p className="text-amber-100 text-lg mb-2">{milestone.label}</p>
                <p className="text-amber-200/70 text-sm">Since {milestone.year}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4">
                Our Core Values
              </h2>
              <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                The principles that guide everything we do
              </p>
            </div>
          </FadeIn>

          <StaggerContainer staggerDelay={0.15}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <StaggerItem key={index}>
                  <motion.div
                    className="p-6 bg-amber-50 rounded-xl hover:bg-amber-100 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                  >
                    <value.icon className="w-10 h-10 text-amber-700 mb-4" />
                    <h3 className="text-xl font-bold text-amber-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600">
                      {value.description}
                    </p>
                  </motion.div>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
